// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { DataCacheToolType, DataCacheToolTypeName, GetGroupByToolType, GetToolTypesByGroup } from '@shared/commons/cache/DataCacheToolEnums';
import { TerceraChartAction, TerceraChartActionEnum } from '@front/chart/TerceraChartAction';
import { TerceraChartActionProcessor } from '@front/chart/TerceraChartActionProcessor/TerceraChartActionProcessor';
import { Resources } from '@shared/localizations/Resources';
import TerceraChartToolsPanelTemplate from '@front/templates/TerceraChartToolsPanelTemplate.html';
import { ContainerControl } from './ContainerControl';
import { ControlsTypes } from '../UtilsClasses/FactoryConstants';
import { HtmlScroll } from '@components/webdesktop/elements/Scroll/HtmlScroll';
import { LayersEnum } from '@front/chart/Utils/LayersEnum';
import { NewContextMenuItemType } from '@shared/utils/ContextMenuUtils';
export class TerceraChartToolsPanel extends ContainerControl {
    constructor() {
        super(...arguments);
        this.toolsScroll = null;
        this.lastPosition = true;
    }
    getType() {
        return ControlsTypes.TerceraChartToolsPanel;
    }
    oninit() {
        super.oninit();
        this.observe('actionProcessor', this.onActionProcessorChanged, { init: false });
        this.on('onMovingBtnClick', this.onMovingBtnClick);
        this.on('onMouseUp', this.onMouseUp);
        this.observe('left top', this.onDragToolbar);
        this.setBounds(0, TerceraChartToolsPanel.MovingSize, TerceraChartToolsPanel.MovingSize, TerceraChartToolsPanel.MovingSize);
    }
    oncomplete() {
        super.oncomplete();
        this.lastPosition = this.get('isVertical');
        this.toolsScrollAdd();
    }
    onDragToolbar() {
        if (this.get('onProcessMove') === false) {
            return;
        }
        const x = this.get('left');
        const y = this.get('top');
        const container = this.getClientPanel();
        if (isNullOrUndefined(container)) {
            return;
        }
        const bottom = container.offsetHeight - y;
        const right = container.offsetWidth - x;
        const isVertical = (x < y && x < bottom) || (right < y && right < bottom);
        void this.set({
            isVertical,
            isLeftOrTop: (x < right && isVertical) || (y < bottom && !isVertical)
        });
    }
    recalculateLocation() {
        const container = this.getClientPanel();
        if (isNullOrUndefined(container)) {
            return;
        }
        const offsetTop = 0;
        const w = container.offsetWidth - TerceraChartToolsPanel.MovingSize;
        const h = container.offsetHeight - TerceraChartToolsPanel.MovingSize;
        const isVertical = this.get('isVertical');
        const isLeftOrTop = this.get('isLeftOrTop');
        const x = !isLeftOrTop && isVertical ? w : 0;
        const y = isVertical ? offsetTop : isLeftOrTop ? offsetTop : h;
        this.setLocation(x, y);
    }
    onMovingBtnClick(event) {
        this.onMouseDown(event, true);
    }
    onMouseUp() {
        void this.set('onProcessMove', false);
    }
    onItemClicked(item) {
        if (!isNullOrUndefined(item === null || item === void 0 ? void 0 : item.event))
            item.event(item.type);
    }
    onToolStripItemPointerEnter(toolStripItem) {
        void this.set('hoveredToolStripItem', toolStripItem);
    }
    onToolStripItemPointerLeave(toolStripItem) {
        void this.set('hoveredToolStripItem', null);
    }
    showDropDownMenu(toolStripItem, event) {
        var _a;
        if (this.get('openedToolStripItem') === toolStripItem) {
            return;
        }
        const type = (_a = toolStripItem === null || toolStripItem === void 0 ? void 0 : toolStripItem.type) === null || _a === void 0 ? void 0 : _a.Details;
        const groupType = GetGroupByToolType(type);
        const toolTypes = GetToolTypesByGroup(groupType);
        const menuItems = [];
        let longestText = '';
        for (let i = 0; i < toolTypes.length; i++) {
            const toolType = toolTypes[i];
            const toolAction = TerceraChartAction.Create(TerceraChartActionEnum.CreationTool, toolType);
            const textKey = TerceraChartActionProcessor.ToolStateInfoDict[toolType].locKey;
            const txt = Resources.getResource(textKey);
            if (txt.length > longestText.length) {
                longestText = txt;
            }
            menuItems.push({
                type: NewContextMenuItemType.Default,
                text: textKey,
                tooltip: textKey,
                selected: toolType === type,
                value: toolType,
                svgImage: DataCacheToolTypeName[toolType],
                event: (item) => {
                    this.onToolStripItemClicked(toolAction);
                }
            });
        }
        const isVertical = this.get('isVertical');
        const isLeftOrTop = this.get('isLeftOrTop');
        const isLeft = isLeftOrTop && isVertical;
        const isTop = isLeftOrTop && !isVertical;
        const isRight = !isLeftOrTop && isVertical;
        const arrowNode = event.node;
        const arrowRect = arrowNode.getBoundingClientRect();
        let x;
        let y;
        let isXRight = false;
        let isYBottom = false;
        if (isLeft) {
            x = arrowRect.right;
            y = arrowRect.top;
        }
        else if (isTop) {
            x = arrowRect.left;
            y = arrowRect.bottom;
        }
        else if (isRight) {
            x = arrowRect.left;
            y = arrowRect.top;
            isXRight = true;
        }
        else {
            x = arrowRect.left;
            y = arrowRect.top;
            isYBottom = true;
        }
        showContextMenu({
            x,
            y,
            isXRight,
            isYBottom,
            items: menuItems,
            functions: {
                onContextMenuInitialized: () => {
                    void this.set('openedToolStripItem', toolStripItem);
                },
                closeCallback: () => {
                    this.hideDropDownMenu();
                },
                onMouseLeave: (mouseLeaveEvent) => {
                    if (!arrowRect.contains(mouseLeaveEvent.x, mouseLeaveEvent.y)) {
                        hideContextMenu();
                    }
                }
            }
        });
    }
    hideDropDownMenu() {
        void this.set('openedToolStripItem', null);
    }
    createDefaultItems() {
        const callback = this.onToolStripItemClicked.bind(this);
        return [
            {
                visible: true,
                name: 'Pointer',
                type: TerceraChartAction.Create(TerceraChartActionEnum.Cancel),
                event: callback
            },
            {
                visible: true,
                name: 'ToolSelector',
                type: TerceraChartAction.Create(TerceraChartActionEnum.ToolSelector),
                event: callback
            },
            {
                visible: true,
                name: 'SnapToCandle',
                type: TerceraChartAction.Create(TerceraChartActionEnum.SnapToCandle),
                event: callback
            },
            {
                visible: true,
                name: 'SetStayInDrawingMode',
                type: TerceraChartAction.Create(TerceraChartActionEnum.SetStayInDrawingMode),
                event: callback
            },
            {
                visible: true,
                name: DataCacheToolTypeName[DataCacheToolType.HorizontalLine],
                type: TerceraChartAction.Create(TerceraChartActionEnum.CreationTool, DataCacheToolType.HorizontalLine),
                event: callback
            },
            {
                visible: true,
                name: DataCacheToolTypeName[DataCacheToolType.Triangle],
                type: TerceraChartAction.Create(TerceraChartActionEnum.CreationTool, DataCacheToolType.Triangle),
                event: callback
            },
            {
                visible: true,
                name: DataCacheToolTypeName[DataCacheToolType.FibonacciRetracement],
                type: TerceraChartAction.Create(TerceraChartActionEnum.CreationTool, DataCacheToolType.FibonacciRetracement),
                event: callback
            },
            {
                visible: true,
                name: DataCacheToolTypeName[DataCacheToolType.GannLine],
                type: TerceraChartAction.Create(TerceraChartActionEnum.CreationTool, DataCacheToolType.GannLine),
                event: callback
            },
            {
                visible: true,
                name: DataCacheToolTypeName[DataCacheToolType.ElliottImpulse],
                type: TerceraChartAction.Create(TerceraChartActionEnum.CreationTool, DataCacheToolType.ElliottImpulse),
                event: callback
            },
            {
                visible: true,
                name: DataCacheToolTypeName[DataCacheToolType.InfoLine],
                type: TerceraChartAction.Create(TerceraChartActionEnum.CreationTool, DataCacheToolType.InfoLine),
                event: callback
            },
            {
                visible: true,
                name: 'DeleteHoverAndSelectedTools',
                type: TerceraChartAction.Create(TerceraChartActionEnum.DeleteHoverAndSelectedTools),
                event: callback
            }
        ];
    }
    onActionProcessorChanged(newProc, oldProc) {
        if (!isNullOrUndefined(oldProc))
            oldProc.StateChanged.UnSubscribe(this.updateToolbar, this);
        if (!isNullOrUndefined(newProc))
            newProc.StateChanged.Subscribe(this.updateToolbar, this);
        if (isNullOrUndefined(newProc))
            return;
        this.updateToolbar();
    }
    onResize() {
        var _a;
        if (!this.rendered) {
            return null;
        }
        const parentSize = this.getParentSideSize();
        if (this.get('maxSize') !== parentSize) {
            void this.set({ maxSize: parentSize });
            const items = (_a = this.get('toolStipItems')) !== null && _a !== void 0 ? _a : [];
            if (items.length === 0)
                return;
            this.toolsScrollUpdate();
        }
        this.recalculateLocation();
    }
    getParentSideSize() {
        let parentSize = 0;
        if (this.get('isVertical')) {
            parentSize = this.el.parentElement.offsetHeight;
            parentSize -= TerceraChartToolsPanel.MoveSeporatorHeight + TerceraChartToolsPanel.ChartBodyGap;
        }
        else {
            parentSize = this.el.parentElement.offsetWidth;
            parentSize -= TerceraChartToolsPanel.MoveSeporatorWidth;
        }
        parentSize -= TerceraChartToolsPanel.OutPadding * 2;
        return parentSize;
    }
    toolsScrollAdd() {
        const tools = this.find('.tool-strip-items-container');
        if (!isNullOrUndefined(tools)) {
            const axis = this.get('isVertical') ? 'y' : 'x';
            this.toolsScroll = HtmlScroll.addScroll(tools, axis);
        }
    }
    toolsScrollUpdate() {
        var _a;
        const tools = this.find('.tool-strip-items-container');
        if (!isNullOrUndefined(tools)) {
            const curPosition = this.get('isVertical');
            if (curPosition !== this.lastPosition) {
                this.lastPosition = curPosition;
                const axis = curPosition ? 'y' : 'x';
                (_a = this.toolsScroll) === null || _a === void 0 ? void 0 : _a.destroy();
                this.toolsScroll = HtmlScroll.addScroll(tools, axis);
            }
            else {
                this.toolsScroll.update();
            }
        }
    }
    updateToolbar() {
        const actionProcessor = this.get('actionProcessor');
        if (isNullOrUndefined(actionProcessor))
            return;
        let toolStripItems = this.get('toolStipItems');
        // First init for optimization purposes.
        if (isNullOrUndefined(toolStripItems))
            toolStripItems = this.createDefaultItems();
        const len = toolStripItems.length;
        for (let i = 0; i < len; i++) {
            const tsItem = toolStripItems[i];
            const action = tsItem.type;
            const state = actionProcessor.GetTerceraChartActionState(action);
            tsItem.active = state.Active;
            tsItem.enabled = state.Enabled;
            tsItem.visible = state.Visible;
            tsItem.tooltip = state.locKey;
            tsItem.isGroup = GetGroupByToolType(action.Details) !== DataCacheToolType.None;
        }
        actionProcessor.chart.IsDirty(LayersEnum.Tools);
        void this.set('toolStipItems', toolStripItems);
        this.onResize();
    }
    onToolStripItemClicked(action) {
        this.onGroupBtnClicked(action);
        this.get('actionProcessor').ProcessTerceraChartAction(action);
    }
    onGroupBtnClicked(action) {
        var _a;
        const toolType = action.Details;
        const groupType = GetGroupByToolType(toolType);
        if (isNullOrUndefined(groupType))
            return;
        const items = ((_a = this.get('toolStipItems')) !== null && _a !== void 0 ? _a : []);
        const newName = DataCacheToolTypeName[toolType];
        void this.set('toolStipItems', items.map((item) => {
            var _a;
            const itemToolType = (_a = item.type) === null || _a === void 0 ? void 0 : _a.Details;
            if (groupType === GetGroupByToolType(itemToolType) && toolType !== itemToolType) {
                item.name = newName;
                item.type = TerceraChartAction.Create(TerceraChartActionEnum.CreationTool, toolType);
                item.event = this.onToolStripItemClicked.bind(this);
            }
            return item;
        }));
    }
    dispose() {
        var _a;
        const actionProcessor = this.get('actionProcessor');
        if (!isNullOrUndefined(actionProcessor)) {
            actionProcessor.StateChanged.UnSubscribe(this.updateToolbar, this);
        }
        (_a = this.toolsScroll) === null || _a === void 0 ? void 0 : _a.destroy();
        super.dispose();
    }
    themeChange() {
        this.updateToolbar();
    }
    localize() { }
}
TerceraChartToolsPanel.OutPadding = 0;
TerceraChartToolsPanel.ChartBodyGap = 8;
TerceraChartToolsPanel.MoveSeporatorHeight = 32;
TerceraChartToolsPanel.MoveSeporatorWidth = 32;
TerceraChartToolsPanel.MovingSize = 48;
ContainerControl.extendWith(TerceraChartToolsPanel, {
    template: TerceraChartToolsPanelTemplate,
    data() {
        return {
            isVertical: true,
            isLeftOrTop: true,
            movable: true,
            isPosAbsolute: true,
            actionProcessor: null,
            maxSize: 0,
            hoveredToolStripItem: null,
            openedToolStripItem: null
        };
    }
});
